import { useEffect, useRef, useState } from "react";
import { MUSIC_DISMISSED_SESSION_KEY, TOP_MUSIC_TRACK } from "../music";

function wasDismissedThisSession() {
  try {
    return window.sessionStorage.getItem(MUSIC_DISMISSED_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function MusicControl() {
  const controlRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [hidden, setHidden] = useState(wasDismissedThisSession);
  const [playing, setPlaying] = useState(false);
  const [available, setAvailable] = useState(__WMLSTI_HAS_ZHIZU_AUDIO__);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !available || hidden) return;

    let cancelled = false;

    const removeInteractionFallback = () => {
      document.removeEventListener("click", startAfterInteraction, true);
      document.removeEventListener("keydown", startAfterInteraction, true);
    };

    const playAfterInteraction = () => {
      void audio.play().then(removeInteractionFallback).catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError")) return;
        setAvailable(false);
        setPlaying(false);
        removeInteractionFallback();
      });
    };

    function startAfterInteraction(event: Event) {
      const target = event.target;
      if (target instanceof Node && controlRef.current?.contains(target)) return;
      playAfterInteraction();
    }

    void audio.play().catch((error: unknown) => {
      if (cancelled) return;
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        document.addEventListener("click", startAfterInteraction, true);
        document.addEventListener("keydown", startAfterInteraction, true);
        return;
      }
      setAvailable(false);
      setPlaying(false);
    });

    return () => {
      cancelled = true;
      removeInteractionFallback();
    };
  }, [available, hidden]);

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !available) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setAvailable(false);
        setPlaying(false);
      }
      return;
    }
    audio.pause();
  }

  function dismiss() {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    try {
      window.sessionStorage.setItem(MUSIC_DISMISSED_SESSION_KEY, "1");
    } catch {
      // The control still closes when session storage is unavailable.
    }
    setPlaying(false);
    setHidden(true);
  }

  if (hidden) return null;

  return (
    <div ref={controlRef} className={`music-control${available ? "" : " music-control--unavailable"}`} aria-label="页面音乐">
      <audio
        ref={audioRef}
        src={__WMLSTI_HAS_ZHIZU_AUDIO__ ? TOP_MUSIC_TRACK.source : undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => { setAvailable(false); setPlaying(false); }}
      />
      <button
        className="music-control__toggle"
        type="button"
        aria-pressed={playing}
        aria-label={available ? `${playing ? "暂停" : "播放"}《${TOP_MUSIC_TRACK.title}》` : `《${TOP_MUSIC_TRACK.title}》暂不可播放`}
        title={available ? `${playing ? "暂停" : "播放"}《${TOP_MUSIC_TRACK.title}》` : `《${TOP_MUSIC_TRACK.title}》暂不可播放`}
        disabled={!available}
        onClick={togglePlayback}
      >
        <span aria-hidden="true">{playing ? "Ⅱ" : "▶"}</span>
        <span>{TOP_MUSIC_TRACK.title}</span>
      </button>
      <button className="music-control__close" type="button" aria-label="停止并关闭音乐" onClick={dismiss}>×</button>
      <span className="sr-only" aria-live="polite">{available ? (playing ? `正在播放《${TOP_MUSIC_TRACK.title}》` : `《${TOP_MUSIC_TRACK.title}》已暂停`) : `《${TOP_MUSIC_TRACK.title}》暂不可播放`}</span>
    </div>
  );
}
